import {Helmet} from '@dr.pogodin/react-helmet';
import {TabProps, Tabs} from 'platform/components';

import type {ReactNode} from 'react';

import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds} from '@dms/routes';
import {
  DetailTemplateHeader,
  EntityHeader,
  getBusinessCaseStateFlag,
  Main,
  NavigationItem,
  Page,
  SnippetProps,
} from '@dms/shared';

import {useNavigate} from 'shared/web';

import {businessCaseSearchParams} from '../../../businessCaseSearchParams';

interface BusinessCaseConceptPageProps {
  customerContent: ReactNode;
  customerHref: string;
  sellingSnippet: SnippetProps;
  buyingSnippet: SnippetProps;
}

export function BusinessCaseConceptPage(props: BusinessCaseConceptPageProps) {
  const navigate = useNavigate();
  const tabs: TabProps[] = [
    {
      title: i18n.t('entity.customer.labels.customer'),
      id: 'customer',
      'data-testid': testIds.businessCase.customer('tabs-customer'),
      content: props.customerContent,
    },
    {
      title: i18n.t('entity.gdpr.labels.consents'),
      id: 'consents',
      'data-testid': testIds.businessCase.customer('tabs-consents'),
      isDisabled: true,
      content: null,
    },
  ];

  const navigation: NavigationItem[] = [
    {
      id: businessCaseSearchParams.businessCaseDetail.overview,
      label: i18n.t('page.businessCase.labels.overview'),
      href: '',
      content: null,
      isDisabled: true,
      hasSeparator: true,
    },
    {
      id: businessCaseSearchParams.create,
      label: i18n.t('entity.customer.labels.customer'),
      href: props.customerHref,
      content: (
        <Main>
          <Tabs
            data-testid={testIds.businessCase.customer('tabs')}
            variant="condensed"
            tabs={tabs}
          />
        </Main>
      ),
      hasCounter: true,
    },
    {
      id: businessCaseSearchParams.businessCaseDetail.buying,
      label: i18n.t('entity.businessCase.labels.buyingOrCommission'),
      href: '',
      content: null,
      isDisabled: true,
      hasCounter: true,
    },
    {
      id: businessCaseSearchParams.businessCaseDetail.selling,
      label: i18n.t('page.businessCase.labels.selling'),
      href: '',
      content: null,
      isDisabled: true,
      hasCounter: true,
    },
    {
      id: businessCaseSearchParams.businessCaseDetail.testDrive,
      label: i18n.t('entity.businessCase.labels.testDrive'),
      href: '',
      content: null,
      isDisabled: true,
      hasCounter: true,
    },
    {
      id: businessCaseSearchParams.businessCaseDetail.reservations,
      label: i18n.t('entity.vehicle.labels.reservations'),
      href: '',
      content: null,
      isDisabled: true,
      hasCounter: true,
    },
    {
      id: businessCaseSearchParams.businessCaseDetail.checkout,
      label: i18n.t('entity.businessCase.labels.checkout'),
      href: '',
      content: null,
      isDisabled: true,
      hasCounter: true,
      hasSeparator: true,
    },
    {
      id: businessCaseSearchParams.businessCaseDetail.documents,
      label: i18n.t('page.businessCase.documents.title'),
      href: '',
      content: null,
      isDisabled: true,
    },
  ];

  const conceptFlag = getBusinessCaseStateFlag('CONCEPT');
  const header: DetailTemplateHeader = {
    title: i18n.t('page.businessCase.labels.newBusinessCase'),
    icon: 'places/business_center',
    flags: conceptFlag ? [conceptFlag] : [],
    parameters: [i18n.t('general.labels.noIdentification')],
    actions: [
      {
        title: i18n.t('general.actions.discard'),
        onClick: () => navigate(businessCaseRoutes.home),
        type: 'button',
        variant: 'secondary',
        'data-testid': testIds.businessCase.concept('discard'),
      },
    ],
    snippets: [
      {
        label: i18n.t('entity.businessCase.labels.contactPerson'),
        placeholder: {title: i18n.t('entity.businessInfo.labels.noContactPerson')},
        isDisabled: true,
      },
      props.sellingSnippet,
      props.buyingSnippet,
    ],
  };

  return (
    <>
      <Helmet title={i18n.t('pages.businessCaseConcept.title')} />
      <Page
        navigation={navigation}
        data-testid={testIds.businessCase.concept('concept')}
        header={
          <EntityHeader
            {...header}
            variant="create"
            data-testid={testIds.businessCase.concept('concept')}
          />
        }
      />
    </>
  );
}
